import { InstallmentNumber } from "../../app/store/product/productOverview/types";
import { Contracts } from "../../constants/Contracts";
import CosmosModels from "../../constants/CosmosModels";
import PaymentMethods from "../../constants/PaymentMethods";
import { Purchases } from "../../constants/Purchases";
import ProductData from "../../entities/product/ProductData";
import BusinessRuleFacade from "../../rules/BusinessRuleFacade";
import PriceService from "../../services/PriceService";
import TariffPackageService from "../../services/TariffPackageService";
import UrlService from "../../services/UrlService";
import { mapSubsidyContractByUrlParam } from "../../utilites/util";
import Validator from "../../validators/Validator";

export default class ProductDataFactory {

  private readonly product: any;
  private readonly pageData: any;
  private readonly urlParams: any;
  private readonly businessRuleFacade: BusinessRuleFacade;

  constructor(product: any, pageData: any) {
    this.product = product;
    this.pageData = pageData;
    this.urlParams = UrlService.getParamsFromUrlOnLoadPage();
    this.businessRuleFacade = BusinessRuleFacade.getInstance();
    this.businessRuleFacade.init(this.product, this.pageData);
  }

  private getDefaultPurchase() {
    if (this.product.sellCosmos && UrlService.isCosmos()) {
      return Purchases.COSMOS;
    }

    if (this.product.sellSubsidy) {
      return Purchases.SUBSIDY;
    }

    if (this.product.sellWithoutContractObligation) {
      return Purchases.SUBSIDY;
    }

    return Purchases.COSMOS;
  }

  private getContractType() {
    if (this.businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
      if (typeof this.urlParams.contractType !== 'undefined'  && this.urlParams.contractType !== "") {
        return this.urlParams.contractType;
      }
      return Contracts.NEW;
    }
    return null;
  }

  private getTp() {
    if (typeof this.urlParams.tariff === 'undefined' && !this.businessRuleFacade.isAccessory() && !this.businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
      const assignedTariffPackagePrices = this.product.assignedTariffPackage.prices[this.getDefaultPurchase()];

      if (Validator.isArray(assignedTariffPackagePrices) && Validator.checkIfArrayHasItems(assignedTariffPackagePrices)) {
        return this.product.assignedTariffPackage;
      }

      return TariffPackageService.getFirstTariffPackageByPurchase(this.product, this.getDefaultPurchase());
    }

    if (typeof this.urlParams.tariff !== 'undefined' && this.businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
      return {
        id: Number(this.urlParams.tariff)
      };
    }

    if (!this.businessRuleFacade.isAccessory() && this.businessRuleFacade.isTpValid()) {
      return TariffPackageService.getTpByTpId(this.product, Number(this.urlParams.tariff), this.getDefaultPurchase());
    }

    return null;
  }

  private getColorId() {
    if (this.businessRuleFacade.isColorIdValid()) {
      return this.urlParams.color;
    }

    const productColorsImages = this.product.productColorsImages;

    return Array.isArray(productColorsImages) && productColorsImages.length ? this.product.productColorsImages[0].color.id : 0;
  }

  private getCosmosPrices() {
    if (this.businessRuleFacade.isAccessory() || this.businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
      return this.product.productPrices.cosmos;
    }

    return this.getTp().prices.cosmos;
  }

  private getSubsidyPrices() {
    if (this.businessRuleFacade.isAccessory() || this.businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
      return this.product.productPrices.subsidy;
    }

    return this.getTp().prices.subsidy;
  }

  private getInstallments(urlParam: string, isCosmos: boolean) {
    if (this.getDefaultPurchase() === Purchases.COSMOS && isCosmos && this.businessRuleFacade.isCosmosInstallmentsValid()) {
      return urlParam;
    }

    if (!isCosmos && this.businessRuleFacade.isContractValid()) {
      return mapSubsidyContractByUrlParam(urlParam);
    }

    let prices: any;

    if (isCosmos) {
      prices = this.getCosmosPrices();
    } else {
      prices = this.getSubsidyPrices();
    }

    const selectedInstallments = prices.find((item: any) => {
      return item.selected;
    });

    if (!isCosmos) {
      if (selectedInstallments) {
        return selectedInstallments.contractDuration ? selectedInstallments.contractDuration : selectedInstallments.installments;
      } else {
        return 24;
      }
    }

    return typeof selectedInstallments !== 'undefined' && typeof selectedInstallments === 'object' ? selectedInstallments.installments : 24;
  }

  private getParticipation(): number {
    if (!this.businessRuleFacade.isCosmosParticipationValid()) {
      return 0;
    }

    const participationParam = Number(this.urlParams.participation);

    if (this.businessRuleFacade.isCosmosParticipationExists()) {
      const cosmosPriceItem = PriceService.getCosmosPriceItem(this.product);

      if (cosmosPriceItem) {
        if (this.businessRuleFacade.isCosmosParticipationLessThenMin(participationParam)) {
          return cosmosPriceItem.cosmosParticipation.min;
        }

        if (this.businessRuleFacade.isCosmosParticipationGreaterThanMax(participationParam)) {
          return cosmosPriceItem.cosmosParticipation.max;
        }

        if (!this.businessRuleFacade.isCosmosParticipationValueInRange(participationParam)) {
          return PriceService.calculateParticipationValue(participationParam, this.product);
        }
      }
    }

    return participationParam;
  }

  private getCosmosModel() {
    if (this.businessRuleFacade.isCosmosModelValid()) {
      return this.urlParams.cosmosModel;
    }

    const cosmosModel = this.product.cosmosModel.find((item: any) => item.selected);

    return !Validator.isUndefined(cosmosModel) && Validator.isObject(cosmosModel) ? cosmosModel.value : CosmosModels.BANK;
  }

  private getPaymentType() {
    if (this.businessRuleFacade.isTvOrCordlessPhoneOrStb()) {
      if (typeof this.urlParams.paymentType !== 'undefined' && this.urlParams.paymentType !== "") {
        return this.urlParams.paymentType;
      }
      return PaymentMethods.INSTALLMENT_OFFER_24;
    }

    return null;
  }

  private getInstallmentsOverBillPeriod(): boolean {
    if (this.businessRuleFacade.isInstallmentsOverBillPeriodValid()) {
      return this.urlParams.installments;
    }

    return false;
  }

  public create() {
    return new ProductData(
      this.getTp() !== null ? this.getTp().id : 0,
      this.getColorId(),
      Number(this.getInstallments(this.urlParams.contract, false)) as InstallmentNumber,
      Number(this.getInstallments(this.urlParams.cosmosInstallments, true)),
      this.getParticipation(),
      this.getCosmosModel(),
      this.getPaymentType(),
      this.getDefaultPurchase(),
      this.getContractType(),
      this.getInstallmentsOverBillPeriod(),
      this.product.productLine.productLine,
      this.product.phoneId,
      true
    );
  }

}
